import React, {useEffect, useRef, useState} from 'react'
import axios from 'axios'
import {
    Alert, Box, Button, FormControl, Grid, IconButton, InputLabel, MenuItem, Modal,
    Paper, Select, TextField, Tooltip
} from '@mui/material'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import PostAddIcon from '@mui/icons-material/PostAdd'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {DataGrid, GridColDef, GridToolbarContainer, GridToolbarQuickFilter} from '@mui/x-data-grid'

const TYPE_LABELS: Record<string, string> = {
    COIN: 'Coin',
    ADVENTURE_EQUIPMENT: 'Adventure Equipment',
    CRAFTING_MATERIAL: 'Crafting Material',
    REAGENT: 'Reagent',
    SUPERIOR_EQUIPMENT: 'Superior Equipment',
    RITUAL_SCROLL: 'Ritual Scroll',
    CATALYST: 'Catalyst',
}
const TYPE_OPTIONS = Object.entries(TYPE_LABELS).map(([value, label]) => ({value, label}))

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 420,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
}

function parseCsv(text: string): string[][] {
    return text
        .split(/\r?\n/)
        .slice(1)
        .filter(line => line.trim().length > 0)
        .map(line => line.split(',').map(cell => cell.trim()))
}

function PanelToolbar({onAdd, onUpload}: {onAdd: () => void; onUpload: (file: File) => void}) {
    const inputRef = useRef<HTMLInputElement>(null)
    return (
        <GridToolbarContainer>
            <GridToolbarQuickFilter/>
            <Tooltip title="Add">
                <Button onClick={onAdd}><PostAddIcon/></Button>
            </Tooltip>
            <Tooltip title="Import from CSV">
                <Button component="label">
                    <UploadFileIcon/>
                    <input ref={inputRef} type="file" accept=".csv" hidden
                        onChange={e => {
                            if (e.target.files?.[0]) { onUpload(e.target.files[0]); e.target.value = '' }
                        }}
                    />
                </Button>
            </Tooltip>
        </GridToolbarContainer>
    )
}

export default function TpItemsAdminPanel() {
    const [items, setItems] = useState<any[]>([])
    const [open, setOpen] = useState(false)
    const [nameError, setNameError] = useState<string | null>(null)
    const [form, setForm] = useState({name: '', type: 'COIN', tp_value: 1})
    const [uploadResult, setUploadResult] = useState<{message: string; error: boolean} | null>(null)

    useEffect(() => {
        axios.get('/admin/tp-items').then(({data}) => setItems(data))
    }, [])

    function handleClose() {
        setOpen(false)
        setNameError(null)
        setForm({name: '', type: 'COIN', tp_value: 1})
    }

    async function create() {
        try {
            const {data} = await axios.post('/admin/tp-items', form)
            setItems(prev => [...prev, data])
            handleClose()
        } catch (err: any) {
            if (err?.response?.status === 409) setNameError(err.response.data?.error ?? 'Name already exists.')
        }
    }

    async function uploadCsv(file: File) {
        setUploadResult(null)
        const text = await file.text()
        const rows = parseCsv(text)
        // CSV format: name,type,tp_value (type = enum key e.g. COIN)
        const parsed = rows
            .filter(r => r.length >= 3 && r[0])
            .map(r => ({
                name: r[0],
                type: r[1].toUpperCase().replace(/\s+/g, '_'),
                tp_value: parseInt(r[2]) || 0,
            }))
            .filter(r => TYPE_LABELS[r.type] && r.tp_value >= 0)
        if (parsed.length === 0) {
            setUploadResult({message: 'No valid rows found. CSV format: name,type,tp_value', error: true})
            return
        }
        try {
            const {data} = await axios.post('/admin/tp-items/batch', {items: parsed})
            const fresh = await axios.get('/admin/tp-items')
            setItems(fresh.data)
            setUploadResult({message: `Imported ${data.imported} item${data.imported === 1 ? '' : 's'} (duplicates skipped).`, error: false})
        } catch {
            setUploadResult({message: 'Upload failed. CSV format: name,type,tp_value', error: true})
        }
    }

    async function remove(id: number) {
        await axios.delete(`/admin/tp-items/${id}`)
        setItems(prev => prev.filter(i => i.id !== id))
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Name', flex: 2, headerAlign: 'center'},
        {field: 'type', headerName: 'Type', flex: 1, headerAlign: 'center',
            valueGetter: (params: any) => TYPE_LABELS[params.row.type] ?? params.row.type},
        {field: 'tp_value', headerName: 'TP Value', width: 100, align: 'center', headerAlign: 'center'},
        {field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete"><span>
                    <IconButton size="small" color="error" onClick={() => remove(params.row.id)}>
                        <DeleteIcon fontSize="small"/>
                    </IconButton>
                </span></Tooltip>
            )
        }
    ]

    return <React.Fragment>
        <Grid container spacing={2} p={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" align="center">TP Item Management</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" align="center">Treasure Policy Items</Typography>
                    {uploadResult && (
                        <Alert severity={uploadResult.error ? 'error' : 'success'} onClose={() => setUploadResult(null)} sx={{mb: 1}}>
                            {uploadResult.message}
                        </Alert>
                    )}
                    <Box sx={{mb: 1}}>
                        <Typography variant="caption" color="text.secondary">
                            CSV format: name,type,tp_value &nbsp;|&nbsp;
                            Types: {Object.entries(TYPE_LABELS).map(([k, v]) => `${k} (${v})`).join(', ')}
                        </Typography>
                    </Box>
                    <DataGrid autoHeight density="compact" rows={items} columns={columns}
                        getRowId={r => r.id}
                        slots={{toolbar: () => <PanelToolbar onAdd={() => setOpen(true)} onUpload={uploadCsv}/>}}
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>
        </Grid>

        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" gutterBottom>Add TP Item</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Name" error={!!nameError} helperText={nameError ?? ''}
                            value={form.name}
                            onChange={e => {setForm({...form, name: e.target.value}); if (nameError) setNameError(null)}}/>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select value={form.type} label="Type"
                                onChange={e => setForm({...form, type: e.target.value})}>
                                {TYPE_OPTIONS.map(({value, label}) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="TP Value" type="number" value={form.tp_value}
                            inputProps={{min: 0}}
                            onChange={e => setForm({...form, tp_value: Math.max(0, parseInt(e.target.value) || 0)})}/>
                    </Grid>
                </Grid>
                <Container sx={{textAlign: 'center', mt: 2}}>
                    <Button variant="contained" disabled={!form.name || !form.type || !!nameError} onClick={create}>Add</Button>
                </Container>
            </Box>
        </Modal>
    </React.Fragment>
}
